<template>
	<div id="cdn" class="mt-5">
		<!-- Upload Area -->
		<div class="upload-container mb-5">
			<div class="upload-header d-flex align-items-center mb-2">
				<b-button
					variant="link"
					@click="isUploadPanelExpanded = !isUploadPanelExpanded"
					class="p-0 me-2"
				>
					<b-icon-chevron-up v-if="!isUploadPanelExpanded" />
					<b-icon-chevron-down v-else />
				</b-button>
				<h5 class="mb-0">Upload Files</h5>
			</div>
			<b-collapse v-model="isUploadPanelExpanded">
				<div 
					class="upload-area" 
					@dragover.prevent="onDragOver"
					@dragleave.prevent="onDragLeave"
					@drop.prevent="onDrop"
					:class="{ dragging: isDragging }"
				>
					<div class="upload-content">
						<b-icon-cloud-upload font-scale="2" />
						<h4>Drag & Drop Files Here</h4>
						<p class="text-muted">or</p>
						<b-button variant="primary" @click="triggerFileInput">
							Select Files
						</b-button>
						<input 
							type="file"
							ref="fileInput"
							multiple
							accept="image/*,video/*,application/pdf"
							@change="onFilesSelected"
							class="file-input"
						/>
						<p class="text-muted mt-2">
							Supported formats: JPG, PNG, GIF, SVG, MP4, PDF
						</p>
					</div>
				</div>

				<b-alert
					v-model="showAlert"
					:variant="alertVariant"
					dismissible
					class="alert-fixed"
				>
					{{ alertMessage }}
				</b-alert>
				<!-- Upload Queue -->
				<div v-if="uploadQueue.length > 0" class="upload-queue mt-3">
					<h5>Upload Queue</h5>
					<div 
						v-for="(file, index) in uploadQueue" 
						:key="index"
						class="upload-item"
						:class="{
							uploading: file.status === 'uploading',
							success: file.status === 'success',
							error: file.status === 'error'
						}"
					>
						<div class="upload-item-content">
							<div class="file-info">
								<template v-if="!isImageFile(file.name)">
									<b-icon-file-earmark />
								</template>
								<template v-else>
									<b-img 
										:src="file.preview"
										height="30"
										width="30"
										class="preview-image"
										@error="handleImageError"
									/>
								</template>
								<div class="file-details">
									<div class="file-name">{{ file.name }}</div>
									<div class="file-size">{{ formatFileSize(file.size) }}</div>
								</div>
							</div>
							<div class="upload-status">
								<template v-if="file.status === 'uploading'">
									<b-spinner small type="grow" />
								</template>
								<template v-else-if="file.status === 'success'">
									<b-icon-check-circle-fill variant="success" />
								</template>
								<template v-else-if="file.status === 'error'">
									<b-icon-x-circle-fill variant="danger" />
									<b-button
										size="sm"
										variant="outline-danger"
										@click="retryUpload(file)"
										class="retry-button"
									>
										Retry
									</b-button>
								</template>
							</div>
						</div>
						<!-- Chunked upload progress UI -->
						<div v-if="file.status === 'uploading' && file.chunkCount">
							<b-progress
								:value="file.progress"
								:max="100"
								class="mt-2"
							/>
							<div class="text-muted small mt-1">
								Chunk {{ file.currentChunk + 1 }} / {{ file.chunkCount }}
							</div>
						</div>
						<!-- End chunked upload progress UI -->
					</div>
				</div>

				<!-- Actions -->
				<div class="actions mt-3">
					<b-button
						variant="outline-primary"
						@click="listFiles"
						:disabled="isUploading"
					>
						<b-icon-arrow-clockwise />
						Refresh List
					</b-button>
				</div>
			</b-collapse>
		</div>

		<!-- File Grid -->
		<div class="file-list">
			<div class="file-list-header mb-3">
				<b-form-input
					v-model="searchQuery"
					placeholder="Search files..."
					class="search-input"
				/>
			</div>

			<div class="file-grid">
				<div 
					v-for="f in filteredFiles"
					:key="f.fileName"
					class="file-card"
				>
					<div class="file-preview">
						<template v-if="isImageFile(f.fileName)">
							<b-img
								:src="f.uri"
								fluid
								height="120"
								class="preview-image"
								@error="handleImageError"
							/>
						</template>
						<template v-else>
							<div class="file-icon">
								<b-icon-file-earmark-text font-scale="2" />
							</div>
						</template>
					</div>
					<div class="file-info">
						<v-row>
							<v-col cols="12">
								<div class="file-name" :title="f.fileName">{{ f.fileName }}</div>
							</v-col>
							<v-col cols="12">
								<div class="file-date">{{ formatDate(f.lastModifiedDateTime) }}</div>
							</v-col>
							<v-col cols="12">
								<div class="file-actions">
									<b-button
										size="sm"
										variant="outline-primary"
										:href="f.uri"
										target="_blank"
										class="action-button"
									>
										<b-icon-box-arrow-up-right />
									</b-button>
									<b-button
										size="sm"
										variant="outline-danger"
										@click="deleteFile(f)"
										class="action-button"
									>
										<b-icon-trash />
									</b-button>
								</div>
							</v-col>
						</v-row>
					</div>
				</div>
			</div>
		</div>

		<!-- Delete Modal -->
		<b-modal
			v-model="showDeleteModal"
			title="Delete File"
			@ok="confirmDelete"
			ok-variant="danger"
			ok-title="Delete"
			cancel-title="Cancel"
		>
			<p>Are you sure you want to delete "{{ fileToDelete ? fileToDelete.fileName : '' }}"?</p>
			<p class="text-muted">This action cannot be undone.</p>
		</b-modal>
	</div>
</template>

<script>
export default {
	name: 'Upload',
	data() {
		return {
			files: null,
			searchQuery: '',
			isDragging: false,
			uploadQueue: [],
			isUploading: false,
			showDeleteModal: false,
			fileToDelete: null,
			supportedTypes: ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.mp4', '.pdf'],
			isUploadPanelExpanded: false,
			alertMessage: '',
			alertVariant: 'success',
			showAlert: false
		}
	},
	computed: {
		filteredFiles() {
			if (!this.files) return []
			const query = this.searchQuery.toLowerCase()
			return this.files.filter(f => 
				f.fileName.toLowerCase().includes(query) &&
				this.supportedTypes.some(type => f.fileName.toLowerCase().endsWith(type))
			)
		}
	},
	methods: {
		triggerFileInput() {
			this.$refs.fileInput.click()
		},
		onFilesSelected(event) {
			const files = Array.from(event.target.files)
			this.addFilesToQueue(files)
			event.target.value = ''
		},
		onDragOver() {
			this.isDragging = true
		},
		onDragLeave() {
			this.isDragging = false
		},
		onDrop(event) {
			this.isDragging = false
			const files = Array.from(event.dataTransfer.files)
			this.addFilesToQueue(files)
		},
		generateGuid() {
			return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
				var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
				return v.toString(16);
			});
		},
		addFilesToQueue(files) {
			files.forEach(file => {
				const fileType = '.' + file.name.split('.').pop().toLowerCase()
				if (!this.supportedTypes.includes(fileType)) {
					this.showMessage(`File type ${fileType} is not supported`, 'danger')
					return
				}

				let preview = null
				if (file.type.startsWith('image/')) {
					preview = URL.createObjectURL(file)
				}

				const CHUNK_SIZE = 1024 * 1024; // 1MB
				const chunkCount = Math.ceil(file.size / CHUNK_SIZE);
				const uploadId = this.generateGuid();

				this.uploadQueue.push({
					file,
					name: file.name,
					size: file.size,
					preview,
					status: 'pending',
					progress: 0,
					chunkCount,
					currentChunk: 0,
					uploadId
				})
			})

			if (!this.isUploading) {
				this.processUploadQueue()
			}
		},
		async processUploadQueue() {
			this.isUploading = true
			for (const item of this.uploadQueue) {
				if (item.status === 'pending' || item.status === 'error') {
					item.status = 'uploading'
					item.progress = 0
					item.currentChunk = 0
					try {
						await this.uploadFileInChunks(item)
						item.status = 'success'
						item.progress = 100
					} catch (error) {
						console.error('Upload error:', error)
						item.status = 'error'
						this.showMessage(`Failed to upload ${item.name}`, 'danger')
					}
				}
			}
			setTimeout(() => {
				this.uploadQueue = this.uploadQueue.filter(item => item.status !== 'success')
				this.isUploading = false
				if (this.uploadQueue.some(item => item.status === 'pending')) {
					this.processUploadQueue()
				}
			}, 2000)
			this.listFiles()
		},
		async uploadFileInChunks(item) {
			const CHUNK_SIZE = 1024 * 1024; // 1MB
			const totalChunks = item.chunkCount;
			for (let i = 0; i < totalChunks; i++) {
				const start = i * CHUNK_SIZE;
				const end = Math.min(item.size, start + CHUNK_SIZE);
				const chunk = item.file.slice(start, end);
				const getBase64 = this.getBase64 || ((file) => {
					return new Promise((resolve, reject) => {
						const reader = new FileReader();
						reader.onload = (e) => resolve(e.target.result.split(',')[1]);
						reader.onerror = reject;
						reader.readAsDataURL(file);
					});
				});
				const base64 = await getBase64(chunk);
				await this.$http.post(
					`${this.$config.ApiUri}/CMS/Files/UploadRequests`,
					{ content: base64, fileName: item.name, chunkIndex: i, totalChunks, UploadId: item.uploadId }
				)
				item.currentChunk = i;
				item.progress = Math.round(((i + 1) / totalChunks) * 100);
			}
		},
		retryUpload(item) {
			item.status = 'pending'
			if (!this.isUploading) {
				this.processUploadQueue()
			}
		},
		async listFiles() {
			try {
				const response = await this.$http.get(`${this.$config.ApiUri}/CMS/Files?ordered=true`)
				this.files = response.data
			} catch (error) {
				console.error('Error loading files:', error)
				this.showMessage('Failed to load file list', 'danger')
			}
		},
		deleteFile(file) {
			this.fileToDelete = file
			this.showDeleteModal = true
			this.showMessage('PLEASE THINK TWICE BEFORE DELETING A FILE!!', 'warning')
		},
		showMessage(message, variant = 'success') {
            console.log('show message... variant: ' + variant);
            this.alertMessage = message;
            this.alertVariant = variant;
            this.showAlert = true;
            if (variant === 'success' || variant === 'warning' || variant === 'info' || variant === 'danger') {
                setTimeout(() => {
                this.showAlert = false;
                }, 5000);
            }
        },
		async confirmDelete() {
			if (!this.fileToDelete) return
			try {
				var url = `${this.$config.ApiUri}/CMS/Files/${this.fileToDelete.fileName}?file=${this.fileToDelete.fileName}`
				console.log('confirmDelete... url: ' + url)
				await this.$http.delete(url)
				this.showMessage(`File "${this.fileToDelete.fileName}" deleted successfully`)
				this.listFiles()
			} catch (error) {
				console.error('Error deleting file:', error)
				this.showMessage('Failed to delete file', 'danger')
			} finally {
				this.showDeleteModal = false
				this.fileToDelete = null
			}
		},
		isImageFile(fileName) {
			const ext = fileName.toLowerCase().split('.').pop()
			return ['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ext)
		},
		formatFileSize(bytes) {
			if (bytes === 0) return '0 Bytes'
			const k = 1024
			const sizes = ['Bytes', 'KB', 'MB', 'GB']
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
		},
		formatDate(dateString) {
			if (!dateString) return ''
			const date = new Date(dateString)
			return date.toLocaleString()
		},
		handleImageError(event) {
			event.target.style.display = 'none'
		}
	},
	mounted() {
		this.listFiles()
	},
	beforeDestroy() {
		this.uploadQueue.forEach(item => {
			if (item.preview) {
				URL.revokeObjectURL(item.preview)
			}
		})
	}
}
</script>

<style scoped>
.upload-container {
	padding: 20px;
	background-color: #fff;
	border-radius: 8px;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.upload-header {
	padding: 0 10px;
}

.upload-header .btn-link {
	color: #6c757d;
	text-decoration: none;
}

.upload-header .btn-link:hover {
	color: #007bff;
}

.upload-area {
	border: 2px dashed #dee2e6;
	border-radius: 8px;
	padding: 40px;
	text-align: center;
	transition: all 0.3s ease;
	background-color: #f8f9fa;
}

.upload-area.dragging {
	border-color: #007bff;
	background-color: #e7f1ff;
}

.upload-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
}

.file-input {
	display: none;
}

.upload-queue {
	background-color: #fff;
	border-radius: 8px;
	padding: 15px;
}

.upload-item {
	padding: 10px;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	margin-bottom: 10px;
}

.upload-item-content {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.file-info {
	display: flex;
	align-items: center;
	gap: 10px;
}

.file-details {
	display: flex;
	flex-direction: column;
}

.file-name {
	font-weight: 500;
	margin-bottom: 5px;
	word-break: break-word;
	line-height: 1.2;
	min-height: 2.4em;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.file-size {
	font-size: 0.875rem;
	color: #6c757d;
}

.upload-status {
	display: flex;
	align-items: center;
	gap: 10px;
}

.file-list {
	padding: 30px;
	padding-top: 0px;
}

.file-list-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.search-input {
	width: 300px;
}

.file-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
	gap: 20px;
	margin-top: 20px;
}

.file-card {
	background-color: #fff;
	border-radius: 8px;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
	overflow: hidden;
	transition: transform 0.2s;
}

.file-card:hover {
	transform: translateY(-2px);
}

.file-preview {
	height: 120px;
	background-color: #f8f9fa;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.preview-image {
	width: 100%;
	height: 100%;
	object-fit: contain;
}

.upload-item .preview-image {
	width: 30px;
	height: 30px;
	object-fit: contain;
}

.file-icon {
	color: #6c757d;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
}

.file-info {
	padding: 15px;
}

.file-date {
	font-size: 0.875rem;
	color: #6c757d;
	margin-bottom: 10px;
}

.file-actions {
	display: flex;
	gap: 8px;
}

.action-button {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 5px;
}

.retry-button {
	padding: 0.25rem 0.5rem;
	font-size: 0.875rem;
}

/* Add transition for collapse */
.collapse {
	transition: all 0.3s ease;
}

.collapse:not(.show) {
	display: none;
}

.collapsing {
	position: relative;
	height: 0;
	overflow: hidden;
	transition: height 0.3s ease;
}
</style>